/**
 * Actor Catalog Management — list, create, edit, change status.
 */
import { actorCatalogService } from '../services/api';
import { sanitize, ACTOR_CATEGORIES } from '../services/utils';

let _container: HTMLElement;
let _actors: any[] = [];
let _showHidden = false;
let _filter = '';

export async function renderActorCatalog(container: HTMLElement): Promise<void> {
  _container = container;
  await loadActors();
  render();
}

async function loadActors(): Promise<void> {
  const { actors } = await actorCatalogService.list(_showHidden);
  _actors = actors;
}

const toggleBtnStyle = (active: boolean) =>
  `padding:4px 12px;border-radius:9999px;font-size:11px;font-weight:500;cursor:pointer;border:1px solid ${active ? 'var(--colorAccent)' : 'var(--colorBorder)'};background:${active ? 'var(--colorAccent)' : 'var(--colorBgCard)'};color:${active ? '#fff' : 'var(--colorTextMuted)'};transition:all .15s`;

function render(): void {
  const q = _filter.toLowerCase();
  const filtered = q ? _actors.filter((a) =>
    a.label.toLowerCase().includes(q) || a.abbr.toLowerCase().includes(q) || (a.description || '').toLowerCase().includes(q) || (a.actor_id || '').toLowerCase().includes(q)
  ) : _actors;

  const grouped: Record<string, any[]> = {};
  filtered.forEach((a) => {
    if (!grouped[a.category]) grouped[a.category] = [];
    grouped[a.category].push(a);
  });

  // Populate toolbar in the header row
  const toolbar = document.getElementById('admin-sub-toolbar');
  if (toolbar) {
    toolbar.innerHTML = `
      <input id="actor-filter" type="text" placeholder="Filter..." value="${sanitize(_filter)}"
        style="padding:4px 12px;border:1px solid var(--colorBorderInput);border-radius:9999px;font-size:11px;background:var(--colorBgInput);color:var(--colorText);width:160px;box-sizing:border-box">
      <button id="toggle-hidden-actors" style="${toggleBtnStyle(_showHidden)}">
        ${_showHidden ? 'Showing Hidden' : 'Show Hidden'}
      </button>
      <button id="add-actor-btn" style="padding:4px 12px;border-radius:9999px;font-size:11px;font-weight:500;cursor:pointer;border:none;background:var(--colorPrimary);color:#fff">+ Add Actor</button>
    `;
  }

  _container.innerHTML = `
    ${Object.keys(grouped).length === 0
      ? `<div style="text-align:center;padding:32px 0;color:var(--colorTextLight);font-size:13px">${q ? 'No actors match your filter.' : 'No actors found.'}</div>`
      : Object.entries(grouped).map(([cat, actors]) => `
        <div style="margin-bottom:20px">
          <div style="font-size:10px;text-transform:uppercase;letter-spacing:.1em;color:var(--colorTextLight);margin-bottom:8px">${ACTOR_CATEGORIES[cat] || cat} (${actors.length})</div>
          <div style="display:grid;gap:4px">
            ${actors.map((a) => renderActorRow(a)).join('')}
          </div>
        </div>
      `).join('')}
  `;

  document.getElementById('toggle-hidden-actors')?.addEventListener('click', async () => {
    _showHidden = !_showHidden;
    await loadActors();
    render();
  });

  document.getElementById('actor-filter')?.addEventListener('input', (e) => {
    _filter = (e.target as HTMLInputElement).value;
    render();
    // Restore cursor position
    const input = document.getElementById('actor-filter') as HTMLInputElement;
    if (input) { input.focus(); input.selectionStart = input.selectionEnd = input.value.length; }
  });

  document.getElementById('add-actor-btn')?.addEventListener('click', showCreateActorModal);

  document.querySelectorAll('[data-edit-actor]').forEach((el) => {
    el.addEventListener('click', () => {
      const id = (el as HTMLElement).dataset.editActor!;
      const actor = _actors.find((a) => a._id === id);
      if (actor) showEditActorModal(actor);
    });
  });
}

function renderActorRow(a: any): string {
  const statusColors: Record<string, string> = { active: 'var(--colorSuccess)', hidden: 'var(--colorWarning)', deleted: 'var(--colorError)' };
  const badgeColor = BADGE_COLORS.find((c) => c.id === (a.default_badge_color || 'default'));
  const badgeBg = badgeColor?.color ? `background:${badgeColor.color};color:#fff;border-color:${badgeColor.color}` : 'background:var(--colorBgCard);border:1px solid var(--colorBorder);color:var(--colorTextHeading)';
  return `
    <div data-edit-actor="${a._id}" style="display:flex;align-items:center;gap:10px;padding:8px 12px;border-radius:6px;background:var(--colorBgElevated);border:1px solid var(--colorBorder);cursor:pointer;transition:border-color .15s;${a.status !== 'active' ? 'opacity:.6;' : ''}" onmouseover="this.style.borderColor='var(--colorAccent)'" onmouseout="this.style.borderColor='var(--colorBorder)'">
      <span style="width:28px;height:28px;display:flex;align-items:center;justify-content:center;border-radius:4px;font-size:9px;font-weight:700;flex-shrink:0;${badgeBg}">${sanitize(a.abbr)}</span>
      <div style="flex:1;min-width:0">
        <div style="font-size:12px;font-weight:500;color:var(--colorTextHeading)">${sanitize(a.label)}</div>
        <div style="font-size:10px;color:var(--colorTextMuted);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${sanitize(a.description)}</div>
      </div>
      <div style="display:flex;align-items:center;gap:6px;flex-shrink:0">
        ${a.known_vendors?.length ? `<span style="font-size:9px;color:var(--colorTextLight)">${a.known_vendors.length} vendors</span>` : ''}
        <span style="font-size:9px;padding:2px 6px;border-radius:4px;color:${statusColors[a.status] || 'var(--colorTextLight)'};border:1px solid ${statusColors[a.status] || 'var(--colorBorder)'}">${a.status}</span>
      </div>
    </div>
  `;
}

const labelStyle = 'font-size:11px;text-transform:uppercase;letter-spacing:.08em;color:var(--colorTextLight);margin-bottom:6px';
const chipBase = 'display:inline-flex;align-items:center;padding:5px 14px;border-radius:9999px;font-size:13px;cursor:pointer;border:1px solid var(--colorBorder);transition:all .15s;user-select:none';
const chipActive = 'background:var(--colorAccent);color:#fff;border-color:var(--colorAccent)';
const chipInactive = 'background:var(--colorBgCard);color:var(--colorTextMuted)';

const BADGE_COLORS = [
  { id: 'default', label: 'Default', color: '', token: '' },
  { id: 'blue', label: 'Blue', color: '#3b82f6', token: 'var(--colorAccent)' },
  { id: 'green', label: 'Green', color: '#10b981', token: 'var(--colorSuccess)' },
  { id: 'amber', label: 'Amber', color: '#f59e0b', token: 'var(--colorWarning)' },
  { id: 'red', label: 'Red', color: '#ef4444', token: 'var(--colorError)' },
  { id: 'purple', label: 'Purple', color: '#8b5cf6', token: '' },
  { id: 'pink', label: 'Pink', color: '#ec4899', token: '' },
  { id: 'cyan', label: 'Cyan', color: '#06b6d4', token: '' },
  { id: 'orange', label: 'Orange', color: '#f97316', token: '' },
  { id: 'slate', label: 'Slate', color: '#64748b', token: '' },
  { id: 'dark', label: 'Dark', color: '#1e293b', token: '' },
];

// Track vendors list for the modal
let _modalVendors: string[] = [];

function actorFormModal(title: string, data: any, onSubmit: (form: HTMLFormElement) => Promise<void>): void {
  _modalVendors = [...(data.known_vendors || [])];

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };
  const modal = document.createElement('div');
  modal.className = 'modal-box';
  modal.style.maxWidth = '700px';
  const currentColor = data.default_badge_color || 'default';
  const currentStatus = data.status || 'active';
  modal.innerHTML = `
    <form id="actor-modal-form">
      <!-- Actor card preview layout -->
      <div style="display:flex;align-items:flex-start;gap:16px;padding:20px;border-radius:10px;background:var(--colorBgElevated);border:1px solid var(--colorBorder);margin-bottom:20px">
        <div style="display:flex;flex-direction:column;align-items:center;gap:6px;flex-shrink:0">
          <input id="af-abbr" value="${sanitize(data.abbr || '')}" placeholder="ABR" required maxlength="5"
            style="width:64px;height:64px;text-align:center;font-size:15px;font-weight:700;border-radius:10px;border:1px solid var(--colorBorder);background:var(--colorBgCard);color:var(--colorTextHeading);letter-spacing:.03em">
          ${data.actor_id ? `<span style="font-size:9px;color:var(--colorTextLight);font-family:monospace">${sanitize(data.actor_id)}</span>` : `<input id="af-id" value="" placeholder="actor-id" required style="font-size:10px;width:100px;text-align:center;border:1px solid var(--colorBorderInput);border-radius:4px;padding:4px;background:var(--colorBgInput);color:var(--colorTextMuted);font-family:monospace">`}
        </div>
        <div style="flex:1;min-width:0">
          <input id="af-label" value="${sanitize(data.label || '')}" placeholder="Actor Label" required
            style="font-size:18px;font-weight:500;color:var(--colorTextHeading);border:none;border-bottom:1px solid var(--colorBorderInput);background:transparent;width:100%;padding:6px 0;margin-bottom:10px;outline:none"
            onfocus="this.style.borderBottomColor='var(--colorAccent)'" onblur="this.style.borderBottomColor='var(--colorBorderInput)'">
          <input id="af-desc" value="${sanitize(data.description || '')}" placeholder="Short description..."
            style="font-size:14px;color:var(--colorTextMuted);border:none;border-bottom:1px solid transparent;background:transparent;width:100%;padding:4px 0;outline:none"
            onfocus="this.style.borderBottomColor='var(--colorBorderInput)'" onblur="this.style.borderBottomColor='transparent'">
        </div>
      </div>

      <!-- Category (single-select tags) -->
      <div style="margin-bottom:18px">
        <div style="${labelStyle}">Category</div>
        <div id="af-cat-tags" style="display:flex;flex-wrap:wrap;gap:6px">
          ${Object.entries(ACTOR_CATEGORIES).map(([k, v]) =>
            `<span data-cat="${k}" style="${chipBase};${data.category === k ? chipActive : chipInactive}">${v}</span>`
          ).join('')}
        </div>
        <input type="hidden" id="af-cat" value="${data.category || 'request-chain'}">
      </div>

      <!-- Role (single-select tags) -->
      <div style="margin-bottom:18px">
        <div style="${labelStyle}">Role</div>
        <div id="af-role-tags" style="display:flex;flex-wrap:wrap;gap:6px">
          <span data-role="primary" style="${chipBase};${(data.role || 'primary') === 'primary' ? chipActive : chipInactive}">Primary (lifeline)</span>
          <span data-role="attached" style="${chipBase};${data.role === 'attached' ? chipActive : chipInactive}">Attached (sub-component)</span>
        </div>
        <input type="hidden" id="af-role" value="${data.role || 'primary'}">
      </div>

      <!-- Vendors + Badge Color side by side -->
      <div style="display:grid;grid-template-columns:1fr auto;gap:20px;margin-bottom:18px">
        <!-- Vendors / Tools (list) -->
        <div>
          <div style="${labelStyle}">Vendors / Tools</div>
          <div id="af-vendor-list" style="margin-bottom:8px"></div>
          <div style="display:flex;gap:6px">
            <input id="af-vendor-input" placeholder="Add vendor..." style="flex:1;padding:8px 12px;border:1px solid var(--colorBorderInput);border-radius:6px;font-size:13px;background:var(--colorBgInput);color:var(--colorText)">
            <button type="button" id="af-vendor-add" style="padding:8px 14px;border-radius:6px;font-size:12px;font-weight:500;cursor:pointer;border:none;background:var(--colorAccent);color:#fff">Add</button>
          </div>
          <div style="font-size:9px;color:var(--colorTextLight);margin-top:4px">Drag to reorder. Appear as suggestions in the vendor picker.</div>
        </div>
        <!-- Badge Color -->
        <div>
          <div style="${labelStyle}">Badge Color</div>
          <div id="af-color-grid" style="display:grid;grid-template-columns:repeat(4,1fr);gap:4px">
            ${BADGE_COLORS.map((c) => {
              const bg = c.token || c.color || '';
              const style = bg
                ? `background:${bg};border:2px solid ${currentColor === c.id ? 'var(--colorAccent)' : 'transparent'}`
                : `background:var(--colorBgCard);border:2px solid ${currentColor === c.id ? 'var(--colorAccent)' : 'var(--colorBorder)'}`;
              return `<div data-badge-color="${c.id}" style="width:24px;height:24px;border-radius:6px;cursor:pointer;${style};transition:border-color .15s" title="${c.label}"></div>`;
            }).join('')}
          </div>
          <input type="hidden" id="af-badge-color" value="${currentColor}">
        </div>
      </div>

      <!-- Status (Active / Hidden) -->
      ${data._id ? `
      <div style="margin-bottom:20px">
        <div style="${labelStyle}">Status</div>
        <div id="af-status-tags" style="display:flex;gap:6px">
          <span data-status-val="active" style="${chipBase};${currentStatus === 'active' ? 'background:var(--colorSuccess);color:#fff;border-color:var(--colorSuccess)' : chipInactive}">Active</span>
          <span data-status-val="hidden" style="${chipBase};${currentStatus === 'hidden' ? 'background:var(--colorWarning);color:#fff;border-color:var(--colorWarning)' : chipInactive}">Hidden</span>
        </div>
        <input type="hidden" id="af-status" value="${currentStatus}">
        <div style="font-size:9px;color:var(--colorTextLight);margin-top:4px">Hidden actors won't appear in the palette but are preserved in existing worksheets.</div>
      </div>
      ` : ''}

      <!-- Actions -->
      <div style="display:flex;gap:8px">
        <button type="button" onclick="this.closest('.modal-overlay').remove()" style="flex:1;padding:12px;border-radius:8px;font-size:14px;cursor:pointer;border:1px solid var(--colorBorder);background:var(--colorBgElevated);color:var(--colorText)">Cancel</button>
        <button type="submit" style="flex:1;padding:12px;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;border:none;background:var(--colorPrimary);color:#fff">${data._id ? 'Save' : 'Create'}</button>
      </div>
    </form>
  `;
  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  // Category tag selection
  document.querySelectorAll('#af-cat-tags span').forEach((el) => {
    el.addEventListener('click', () => {
      document.querySelectorAll('#af-cat-tags span').forEach((s) => {
        (s as HTMLElement).style.cssText = chipBase + ';' + chipInactive;
      });
      (el as HTMLElement).style.cssText = chipBase + ';' + chipActive;
      (document.getElementById('af-cat') as HTMLInputElement).value = (el as HTMLElement).dataset.cat!;
    });
  });

  // Role tag selection
  document.querySelectorAll('#af-role-tags span').forEach((el) => {
    el.addEventListener('click', () => {
      document.querySelectorAll('#af-role-tags span').forEach((s) => {
        (s as HTMLElement).style.cssText = chipBase + ';' + chipInactive;
      });
      (el as HTMLElement).style.cssText = chipBase + ';' + chipActive;
      (document.getElementById('af-role') as HTMLInputElement).value = (el as HTMLElement).dataset.role!;
    });
  });

  // Badge color selection — also updates the badge preview
  document.querySelectorAll('#af-color-grid div').forEach((el) => {
    el.addEventListener('click', () => {
      const colorId = (el as HTMLElement).dataset.badgeColor!;
      const colorDef = BADGE_COLORS.find((c) => c.id === colorId);
      document.querySelectorAll('#af-color-grid div').forEach((d) => {
        (d as HTMLElement).style.borderColor = 'transparent';
      });
      (el as HTMLElement).style.borderColor = 'var(--colorAccent)';
      (document.getElementById('af-badge-color') as HTMLInputElement).value = colorId;
      // Update badge preview
      const badge = document.getElementById('af-abbr') as HTMLElement;
      if (badge && colorDef) {
        if (colorDef.color) {
          badge.style.background = colorDef.color;
          badge.style.color = '#fff';
          badge.style.borderColor = colorDef.color;
        } else {
          badge.style.background = 'var(--colorBgCard)';
          badge.style.color = 'var(--colorTextHeading)';
          badge.style.borderColor = 'var(--colorBorder)';
        }
      }
    });
  });

  // Apply initial badge color preview
  const initColor = BADGE_COLORS.find((c) => c.id === currentColor);
  const badgeEl = document.getElementById('af-abbr') as HTMLElement;
  if (badgeEl && initColor?.color) {
    badgeEl.style.background = initColor.color;
    badgeEl.style.color = '#fff';
    badgeEl.style.borderColor = initColor.color;
  }

  // Status tag selection (edit mode only)
  document.querySelectorAll('#af-status-tags span').forEach((el) => {
    el.addEventListener('click', () => {
      const val = (el as HTMLElement).dataset.statusVal!;
      const colors: Record<string, string> = { active: 'background:var(--colorSuccess);color:#fff;border-color:var(--colorSuccess)', hidden: 'background:var(--colorWarning);color:#fff;border-color:var(--colorWarning)' };
      document.querySelectorAll('#af-status-tags span').forEach((s) => {
        (s as HTMLElement).style.cssText = chipBase + ';' + chipInactive;
      });
      (el as HTMLElement).style.cssText = chipBase + ';' + (colors[val] || chipInactive);
      (document.getElementById('af-status') as HTMLInputElement).value = val;
    });
  });

  // Vendor list management
  renderVendorList();

  document.getElementById('af-vendor-add')?.addEventListener('click', addVendorFromInput);
  document.getElementById('af-vendor-input')?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); addVendorFromInput(); }
  });

  // Form submit
  document.getElementById('actor-modal-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    await onSubmit(e.target as HTMLFormElement);
    overlay.remove();
    await loadActors();
    render();
  });

  setTimeout(() => (document.getElementById(data._id ? 'af-label' : 'af-id') as HTMLElement)?.focus(), 100);
}

function addVendorFromInput(): void {
  const input = document.getElementById('af-vendor-input') as HTMLInputElement;
  const val = input.value.trim();
  if (!val || _modalVendors.includes(val)) { input.value = ''; return; }
  _modalVendors.push(val);
  input.value = '';
  renderVendorList();
  input.focus();
}

function renderVendorList(): void {
  const container = document.getElementById('af-vendor-list');
  if (!container) return;

  if (_modalVendors.length === 0) {
    container.innerHTML = '<div style="font-size:11px;color:var(--colorTextLight);padding:8px 0;font-style:italic">No vendors added yet.</div>';
    return;
  }

  const cols = _modalVendors.length > 10 ? 3 : _modalVendors.length > 5 ? 2 : 1;
  const rows = cols > 1 ? Math.ceil(_modalVendors.length / cols) : _modalVendors.length;
  const vendorItems = _modalVendors.map((v, i) => `
    <div draggable="true" data-vendor-idx="${i}" style="display:flex;align-items:center;gap:6px;padding:4px 8px;border-radius:5px;background:var(--colorBgCard);border:1px solid var(--colorBorder);cursor:grab;transition:background .1s" onmouseover="this.style.background='var(--colorBgElevated)'" onmouseout="this.style.background='var(--colorBgCard)'">
      <span style="color:var(--colorTextLight);font-size:9px;cursor:grab;flex-shrink:0">⠿</span>
      <span style="flex:1;font-size:11px;color:var(--colorText);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${sanitize(v)}</span>
      <button type="button" data-remove-vendor="${i}" style="color:var(--colorError);background:none;border:none;cursor:pointer;font-size:12px;line-height:1;padding:0 2px;opacity:.5;flex-shrink:0" title="Remove">×</button>
    </div>
  `).join('');

  container.innerHTML = cols > 1
    ? `<div style="display:grid;grid-template-columns:repeat(${cols},1fr);grid-template-rows:repeat(${rows},auto);grid-auto-flow:column;gap:3px">${vendorItems}</div>`
    : `<div style="display:grid;gap:3px">${vendorItems}</div>`;

  // Remove buttons
  container.querySelectorAll('[data-remove-vendor]').forEach((el) => {
    el.addEventListener('click', (e) => {
      e.preventDefault();
      const idx = parseInt((el as HTMLElement).dataset.removeVendor!, 10);
      _modalVendors.splice(idx, 1);
      renderVendorList();
    });
  });

  // Drag-to-reorder
  let _dragIdx: number | null = null;
  container.querySelectorAll('[data-vendor-idx]').forEach((el) => {
    el.addEventListener('dragstart', (e) => {
      _dragIdx = parseInt((el as HTMLElement).dataset.vendorIdx!, 10);
      (el as HTMLElement).style.opacity = '.4';
    });
    el.addEventListener('dragend', () => {
      (el as HTMLElement).style.opacity = '1';
      _dragIdx = null;
    });
    el.addEventListener('dragover', (e) => { (e as DragEvent).preventDefault(); });
    el.addEventListener('drop', (e) => {
      (e as DragEvent).preventDefault();
      const dropIdx = parseInt((el as HTMLElement).dataset.vendorIdx!, 10);
      if (_dragIdx !== null && _dragIdx !== dropIdx) {
        const [moved] = _modalVendors.splice(_dragIdx, 1);
        _modalVendors.splice(dropIdx, 0, moved);
        renderVendorList();
      }
    });
  });
}

/** Get current vendor list from the modal (called by submit handlers). */
function getModalVendors(): string[] {
  return [..._modalVendors];
}

function showCreateActorModal(): void {
  actorFormModal('New Actor Type', {}, async () => {
    await actorCatalogService.create({
      actor_id: (document.getElementById('af-id') as HTMLInputElement).value.trim(),
      label: (document.getElementById('af-label') as HTMLInputElement).value.trim(),
      abbr: (document.getElementById('af-abbr') as HTMLInputElement).value.trim(),
      category: (document.getElementById('af-cat') as HTMLInputElement).value,
      description: (document.getElementById('af-desc') as HTMLInputElement).value.trim(),
      role: (document.getElementById('af-role') as HTMLInputElement).value,
      known_vendors: getModalVendors(),
      default_badge_color: (document.getElementById('af-badge-color') as HTMLInputElement).value,
    } as any);
  });
}

function showEditActorModal(actor: any): void {
  actorFormModal('Edit Actor', actor, async () => {
    await actorCatalogService.update(actor._id, {
      label: (document.getElementById('af-label') as HTMLInputElement).value.trim(),
      abbr: (document.getElementById('af-abbr') as HTMLInputElement).value.trim(),
      description: (document.getElementById('af-desc') as HTMLInputElement).value.trim(),
      known_vendors: getModalVendors(),
      default_badge_color: (document.getElementById('af-badge-color') as HTMLInputElement).value,
    } as any);
    // Update status if changed
    const statusEl = document.getElementById('af-status') as HTMLInputElement;
    if (statusEl && statusEl.value !== actor.status) {
      await actorCatalogService.setStatus(actor._id, statusEl.value as any);
    }
  });
}
